import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import Tooltip from "@material-ui/core/Tooltip";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import CheckIcon from "@material-ui/icons/Check";
import CircularProgress from "@material-ui/core/CircularProgress";
import { useTranslation } from "react-i18next";
import { evaluateResponse, submitHumanScore } from "../../services/chatApi";

const CRITERIA_KEYS = [
  "accuracy",
  "completeness",
  "formatting",
  "relevance",
  "language",
];

const _cache = new Map();

const useStyles = makeStyles(() => ({
  "@keyframes evalFadeIn": {
    from: { opacity: 0, transform: "translateY(6px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  root: {
    marginTop: "8px",
    border: "1px solid #e0e0e0",
    borderRadius: "8px",
    padding: "10px 12px",
    backgroundColor: "#fafafa",
    fontSize: "12px",
    animation: "$evalFadeIn 0.3s ease-out both",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "8px",
    fontWeight: 600,
    fontSize: "11px",
    color: "#666",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  headerCol: {
    flex: 1,
    textAlign: "center",
    "&:first-child": { textAlign: "left" },
  },
  row: {
    display: "flex",
    alignItems: "center",
    marginBottom: "5px",
    "&:last-child": { marginBottom: 0 },
  },
  labelCol: {
    flex: "0 0 110px",
    display: "flex",
    alignItems: "center",
    gap: "3px",
    color: "#555",
    fontSize: "12px",
  },
  helpIcon: {
    fontSize: "13px !important",
    color: "#bbb",
    cursor: "help",
  },
  aiScoreCol: {
    flex: 1,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "3px",
  },
  star: {
    fontSize: "12px",
    color: "#ffc107",
  },
  starEmpty: {
    fontSize: "12px",
    color: "#e0e0e0",
  },
  scoreNum: {
    fontSize: "11px",
    color: "#999",
    marginLeft: "2px",
    minWidth: "16px",
  },
  humanScoreCol: {
    flex: 1,
    display: "flex",
    justifyContent: "center",
    gap: "2px",
  },
  scoreBtn: {
    width: "22px",
    height: "22px",
    border: "1px solid #ddd",
    borderRadius: "3px",
    backgroundColor: "#fff",
    cursor: "pointer",
    fontSize: "11px",
    fontFamily: "inherit",
    color: "#777",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    transition: "all 0.12s ease",
    "&:hover": {
      borderColor: "#999",
      backgroundColor: "#f0f0f0",
    },
  },
  scoreBtnActive: {
    borderColor: "#1976d2",
    backgroundColor: "#e3f2fd",
    color: "#1976d2",
    fontWeight: 600,
  },
  scoreBtnDisabled: {
    opacity: 0.5,
    cursor: "default",
    "&:hover": {
      borderColor: "#ddd",
      backgroundColor: "#fff",
    },
  },
  issuesRow: {
    marginTop: "6px",
    paddingTop: "6px",
    borderTop: "1px solid #eee",
    fontSize: "11px",
    color: "#999",
  },
  issueTag: {
    display: "inline-block",
    padding: "1px 6px",
    borderRadius: "8px",
    backgroundColor: "#fff3e0",
    color: "#e65100",
    fontSize: "10px",
    marginRight: "4px",
    marginBottom: "2px",
  },
  feedbackRow: {
    marginTop: "6px",
    paddingTop: "6px",
    borderTop: "1px solid #eee",
  },
  feedbackInput: {
    width: "100%",
    border: "1px solid #e0e0e0",
    borderRadius: "4px",
    padding: "6px 8px",
    fontSize: "12px",
    fontFamily: "inherit",
    outline: "none",
    resize: "vertical",
    minHeight: "32px",
    maxHeight: "80px",
    boxSizing: "border-box",
    "&:focus": { borderColor: "#bbb" },
    "&::placeholder": { color: "#ccc" },
  },
  submitRow: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: "6px",
  },
  submitBtn: {
    padding: "4px 14px",
    border: "1px solid #1976d2",
    borderRadius: "4px",
    backgroundColor: "#1976d2",
    color: "#fff",
    fontSize: "11px",
    fontWeight: 500,
    fontFamily: "inherit",
    cursor: "pointer",
    transition: "all 0.12s ease",
    "&:hover": { backgroundColor: "#1565c0" },
    "&:disabled": { opacity: 0.5, cursor: "default" },
  },
  loadingRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "12px 0",
    gap: "8px",
    color: "#999",
    fontSize: "12px",
  },
  sentConfirm: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "11px",
    color: "#4caf50",
  },
}));

export default function EvaluationPanel({ usageLogId }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const cached = _cache.get(usageLogId);
  const [aiScores, setAiScores] = useState(cached?.aiScores ?? null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiFetched, setAiFetched] = useState(cached?.aiFetched ?? false);
  const [humanScores, setHumanScores] = useState(cached?.humanScores ?? {});
  const [feedbackText, setFeedbackText] = useState(cached?.feedbackText ?? "");
  const [submitted, setSubmitted] = useState(cached?.submitted ?? false);
  const [submitting, setSubmitting] = useState(false);

  React.useEffect(() => {
    _cache.set(usageLogId, {
      aiScores,
      aiFetched,
      humanScores,
      feedbackText,
      submitted,
    });
  }, [usageLogId, aiScores, aiFetched, humanScores, feedbackText, submitted]);

  const fetchAiScores = useCallback(async () => {
    if (aiFetched || aiLoading || !usageLogId) return;
    setAiLoading(true);
    try {
      const data = await evaluateResponse(usageLogId);
      setAiScores(data);
    } catch {
      setAiScores(null);
    } finally {
      setAiLoading(false);
      setAiFetched(true);
    }
  }, [usageLogId, aiFetched, aiLoading]);

  React.useEffect(() => {
    fetchAiScores();
  }, [fetchAiScores]);

  const setScore = useCallback(
    (key, val) => {
      if (submitted) return;
      setHumanScores((prev) => ({ ...prev, [key]: val }));
    },
    [submitted],
  );

  const allHumanFilled = CRITERIA_KEYS.every((k) => humanScores[k] != null);

  const handleSubmit = useCallback(async () => {
    if (!allHumanFilled || submitting || submitted) return;
    setSubmitting(true);
    try {
      await submitHumanScore(usageLogId, humanScores, feedbackText);
      setSubmitted(true);
    } catch {
      // silently fail
    } finally {
      setSubmitting(false);
    }
  }, [
    allHumanFilled,
    submitting,
    submitted,
    usageLogId,
    humanScores,
    feedbackText,
  ]);

  const renderStars = (score) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <span key={i} className={i <= score ? classes.star : classes.starEmpty}>
          ★
        </span>,
      );
    }
    return stars;
  };

  return (
    <div className={classes.root}>
      {/* Header */}
      <div className={classes.header}>
        <span className={classes.headerCol} style={{ flex: "0 0 110px" }} />
        <span className={classes.headerCol}>
          {t("ai_chat.evaluation.ai_score")}
        </span>
        <span className={classes.headerCol}>
          {t("ai_chat.evaluation.your_score")}
        </span>
      </div>

      {aiLoading && (
        <div className={classes.loadingRow}>
          <CircularProgress size={14} />
          <span>{t("ai_chat.evaluation.evaluating")}</span>
        </div>
      )}

      {!aiLoading && (
        <>
          {/* Criteria rows */}
          {CRITERIA_KEYS.map((key) => (
            <div key={key} className={classes.row}>
              <div className={classes.labelCol}>
                {t(`ai_chat.evaluation.criteria.${key}`)}
                <Tooltip
                  title={t(`ai_chat.evaluation.tooltips.${key}`)}
                  placement="top"
                  arrow
                >
                  <HelpOutlineIcon className={classes.helpIcon} />
                </Tooltip>
              </div>

              {/* AI score */}
              <div className={classes.aiScoreCol}>
                {aiScores ? (
                  <Tooltip
                    title={aiScores.rationale?.[key] || ""}
                    placement="bottom"
                    arrow
                    disableHoverListener={!aiScores.rationale?.[key]}
                  >
                    <span>
                      {renderStars(aiScores[key] || 0)}
                      <span className={classes.scoreNum}>
                        ({aiScores[key] || "—"})
                      </span>
                    </span>
                  </Tooltip>
                ) : (
                  <span className={classes.scoreNum}>—</span>
                )}
              </div>

              {/* Human score */}
              <div className={classes.humanScoreCol}>
                {[1, 2, 3, 4, 5].map((v) => (
                  <button
                    key={v}
                    className={`${classes.scoreBtn} ${
                      humanScores[key] === v ? classes.scoreBtnActive : ""
                    } ${submitted ? classes.scoreBtnDisabled : ""}`}
                    onClick={() => setScore(key, v)}
                    disabled={submitted}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>
          ))}

          {/* Issues */}
          {aiScores?.issues_found?.length > 0 && (
            <div className={classes.issuesRow}>
              <span style={{ marginRight: 4 }}>
                {t("ai_chat.evaluation.issues")}:
              </span>
              {aiScores.issues_found.map((issue) => (
                <span key={issue} className={classes.issueTag}>
                  {issue}
                </span>
              ))}
            </div>
          )}

          {/* Feedback text */}
          {!submitted && (
            <div className={classes.feedbackRow}>
              <textarea
                className={classes.feedbackInput}
                placeholder={t("ai_chat.evaluation.feedback_placeholder")}
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                maxLength={2000}
              />
            </div>
          )}

          {/* Submit */}
          <div className={classes.submitRow}>
            {submitted ? (
              <span className={classes.sentConfirm}>
                <CheckIcon style={{ fontSize: 14 }} />{" "}
                {t("ai_chat.evaluation.submitted")}
              </span>
            ) : (
              <button
                className={classes.submitBtn}
                onClick={handleSubmit}
                disabled={!allHumanFilled || submitting}
              >
                {submitting
                  ? t("ai_chat.evaluation.sending")
                  : t("ai_chat.evaluation.submit")}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

EvaluationPanel.propTypes = {
  usageLogId: PropTypes.number,
};
